import { api } from '@/services/api/AxiosBaseService'
import type { PaginatedResponse } from '@/types/api'

/** Linha da composição: serviço × prestador específico. */
export interface ProductServiceRow {
  id?: string
  serviceId: string
  serviceProviderId: string
  serviceName?: string
  providerName?: string
}

/** Regra de elegibilidade de um grau dentro da modalidade. */
export interface ProductModalityDegreeRow {
  id?: string
  degreeRelationshipId: string
  degree?: {
    id: string
    degreeRelationship: string
    code: string
    gender: string
  }
  minAge: number | null
  maxAge: number | null
  additionalPolicies: number
}

/** Modalidade disponibilizada pelo produto (Individual, Família). */
export interface ProductModalityRow {
  id?: string
  planTypeRelationshipId: string
  planTypeRelationship?: {
    id: string
    name: string
    minAge: number | null
  }
  degrees: ProductModalityDegreeRow[]
}

export interface Product {
  id: string
  name: string
  description: string | null
  status: 'ATIVO' | 'INATIVO'
  services?: ProductServiceRow[]
  modalities?: ProductModalityRow[]
}

/** Payload write — snake_case (backend valida assim). */
export interface ProductWritePayload {
  name?: string
  description?: string | null
  status?: 'ATIVO' | 'INATIVO'
  services?: Array<{
    service_id: string
    service_provider_id: string
  }>
  modalities?: Array<{
    plan_type_relationship_id: string
    degrees?: Array<{
      degree_relationship_id: string
      min_age?: number | null
      max_age?: number | null
      additional_policies?: number
    }>
  }>
}

export const ProductsService = {
  async list(params: Record<string, unknown> = {}): Promise<PaginatedResponse<Product>> {
    const r = await api.get<PaginatedResponse<Product>>('/commercial/products', { params })
    return r.data
  },
  async show(id: string): Promise<Product> {
    const r = await api.get<{ data: Product }>(`/commercial/products/${id}`)
    return r.data.data
  },
  async create(payload: ProductWritePayload): Promise<Product> {
    const r = await api.post<{ data: Product }>('/commercial/products', payload)
    return r.data.data
  },
  async update(id: string, payload: ProductWritePayload): Promise<Product> {
    const r = await api.put<{ data: Product }>(`/commercial/products/${id}`, payload)
    return r.data.data
  },
  async destroy(id: string): Promise<void> {
    await api.delete(`/commercial/products/${id}`)
  },
  async updateStatus(id: string, status: 'ATIVO' | 'INATIVO'): Promise<Product> {
    const r = await api.put<{ data: Product }>(`/commercial/products/${id}/status`, { status })
    return r.data.data
  },
}
